// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import BlogSubscribePrompt from "../NewsletterForm/BlogSubscribePrompt";
import { blogPosts } from "../../consts/blogPosts";
import { blogPostContentPt } from "../../consts/blogPostsPt";
import { estimateReadingMinutes } from "../../utils/blogReadingTime";
import { joyoKanji } from "../../consts/joyoKanji";
import { totalKanjiCount } from "../../consts/allKanjiMeanings";
import { useAuth } from "../../context/AuthContext";
import { useBlogReadProgress } from "../../hooks/useBlogReadProgress";
import { LearnedBadge } from "../LearningStatus/LearningStatus";
import styles from "./blogPage.module.scss";

function formatDate(dateString, locale = "en-US") {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function BlogPage() {
    const { t, i18n } = useTranslation("blogPage");
    const { status: authStatus } = useAuth();
    const { progress: readProgress } = useBlogReadProgress();
    const [searchParams] = useSearchParams();
    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/blog" : "/blog";
    const sortedPosts = [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));
    const counts = {
        joyoCount: joyoKanji.length.toLocaleString("pt-PT"),
        totalCount: totalKanjiCount.toLocaleString("pt-PT"),
    };
    // Tags are kept as their canonical (English) key here - each post card translates
    // its own tags for display, so the same key can drive the `?tag=` filter link below.
    // `keywords` stays the canonical (English) list so it can drive the `?keyword=` filter;
    // `keywordLabels` is the same list in the display language, index for index.
    const posts = isPt
        ? sortedPosts
              .filter((post) =>
                  post.lang === "pt-PT"
                      ? Array.isArray(post.content)
                      : !post.ptSlug && Array.isArray(blogPostContentPt[post.slug]) && blogPostContentPt[post.slug].length > 0
              )
              .map((post) => {
                  if (post.lang === "pt-PT") {
                      return {
                          ...post,
                          path: `/blog/${post.slug}`,
                          keywordLabels: post.keywords,
                          readingMinutes: estimateReadingMinutes(post.content),
                      };
                  }
                  const translatedKeywords = t(`posts.${post.slug}.keywords`, {
                      returnObjects: true,
                      defaultValue: post.keywords,
                  });
                  return {
                      ...post,
                      lang: "pt-PT",
                      path: `/pt/blog/${post.slug}`,
                      title: t(`posts.${post.slug}.title`, { defaultValue: post.title, ...counts }),
                      excerpt: t(`posts.${post.slug}.excerpt`, { defaultValue: post.excerpt, ...counts }),
                      keywordLabels: Array.isArray(translatedKeywords) ? translatedKeywords : post.keywords,
                      readingMinutes: estimateReadingMinutes(blogPostContentPt[post.slug] ?? post.content),
                  };
              })
        : sortedPosts.filter((post) => post.lang !== "pt-PT").map((post) => ({
              ...post,
              path: `/blog/${post.slug}`,
              keywordLabels: post.keywords,
              readingMinutes: estimateReadingMinutes(post.content),
          }));

    const activeTag = searchParams.get("tag");
    const activeKeyword = searchParams.get("keyword");
    const activeKeywordLabel = activeKeyword
        ? posts.map((post) => post.keywordLabels?.[post.keywords?.indexOf(activeKeyword)]).find(Boolean) ?? activeKeyword
        : null;
    const visiblePosts = posts.filter(
        (post) =>
            (!activeTag || post.tags?.includes(activeTag)) &&
            (!activeKeyword || post.keywords?.includes(activeKeyword))
    );

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>
                    <a href="/rss.xml" className={styles.rssLink}>
                        {t("rssFeed")}
                    </a>

                    {(activeTag || activeKeyword) && (
                        <div className={styles.activeFilter}>
                            <span>
                                {t("post.filteringBy", {
                                    tag: activeKeyword
                                        ? `#${activeKeywordLabel}`
                                        : t(`tags.${activeTag}`, { defaultValue: activeTag }),
                                })}
                            </span>
                            <Link to={currentPath} className={styles.clearFilter}>
                                {t("post.clearFilter")}
                            </Link>
                        </div>
                    )}

                    <div className={styles.postList}>
                        {visiblePosts.map((post) => (
                            <article key={post.slug} className={styles.postCard}>
                                <div className={styles.postMeta}>
                                    <time dateTime={post.date}>{formatDate(post.date, isPt ? "pt-PT" : "en-US")}</time>
                                    <span className={styles.postReadingTime}>
                                        <FontAwesomeIcon
                                            icon={faClock}
                                            aria-hidden="true"
                                            className={styles.readingTimeIcon}
                                        />
                                        {t("post.readingTime", { count: post.readingMinutes })}
                                    </span>
                                    {authStatus === "authenticated" && readProgress[post.slug] === "read" && (
                                        <LearnedBadge label={t("post.readBadge")} />
                                    )}
                                    {post.tags?.map((tag) => (
                                        <Link
                                            key={tag}
                                            to={`${currentPath}?tag=${encodeURIComponent(tag)}`}
                                            className={styles.postTag}
                                        >
                                            {t(`tags.${tag}`, { defaultValue: tag })}
                                        </Link>
                                    ))}
                                </div>
                                <h2 className={styles.postTitle}>
                                    <Link to={post.path} className={styles.postCardLink} lang={post.lang}>
                                        {post.title}
                                    </Link>
                                </h2>
                                <p className={styles.postExcerpt}>{post.excerpt}</p>
                                {post.keywords?.length > 0 && (
                                    <div className={styles.keywordRow}>
                                        {post.keywords.map((keyword, index) => (
                                            <Link
                                                key={keyword}
                                                to={`${currentPath}?keyword=${encodeURIComponent(keyword)}`}
                                                className={styles.keyword}
                                            >
                                                #{post.keywordLabels?.[index] ?? keyword}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                <span className={styles.readMore}>{t("readMore")}</span>
                            </article>
                        ))}
                        {(activeTag || activeKeyword) && !visiblePosts.length && (
                            <p className={styles.noResults}>{t("post.noTaggedPosts")}</p>
                        )}
                    </div>

                    <BlogSubscribePrompt />
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default BlogPage;
